import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { runDiagnostic } from '../engine';
import { getNiche, isValidNiche } from '../../../data/diagnostic/niches';
import { DEAL_MODEL_MAP } from '../../../data/diagnostic/dealModels';
import { DIAGNOSTIC_NUMBER_MAX, sanitizeFiniteNumber } from '../../../utils/inputSecurity';

const STORAGE_KEY = 'marketdir.diagnostic.v1';
const MAX_STORAGE_BYTES = 8000;

export const STEP = {
  NICHE: 'niche',
  DEAL_MODEL: 'dealModel',
  NUMBERS: 'numbers',
  GOAL: 'goal',
  RESULT: 'result',
};

export const STEP_ORDER = [STEP.NICHE, STEP.DEAL_MODEL, STEP.NUMBERS, STEP.GOAL, STEP.RESULT];

export const STEP_LABELS = {
  [STEP.NICHE]: 'Чем занимаетесь',
  [STEP.DEAL_MODEL]: 'Как платят',
  [STEP.NUMBERS]: 'Ваши цифры',
  [STEP.GOAL]: 'Цель',
  [STEP.RESULT]: 'Результат',
};

const ALLOWED_FIELDS = new Set([
  'dealModelId',
  'revenue',
  'sales',
  'leads',
  'frequency',
  'keyMetric',
  'optionalMetric',
  'adBudget',
  'targetRevenue',
  'targetMonths',
]);

const ALLOWED_LEVERS = new Set(['l', 'c', 'p', 'q']);
const ALLOWED_MONTHS = new Set([3, 6, 12]);

const NUMERIC_FIELDS = new Set([
  'revenue',
  'sales',
  'leads',
  'frequency',
  'keyMetric',
  'optionalMetric',
  'adBudget',
  'targetRevenue',
]);

const initialState = {
  step: STEP.NICHE,
  nicheId: null,
  dealModelId: null,
  revenue: '',
  sales: '',
  leads: '',
  frequency: '',
  keyMetric: '',
  optionalMetric: '',
  adBudget: '',
  targetRevenue: '',
  targetMonths: 6,
  /** Ручные значения слайдеров «что если», в долях прироста. */
  simulation: {},
};

const sanitizeNumericField = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = sanitizeFiniteNumber(value, { min: 0, max: DIAGNOSTIC_NUMBER_MAX, fallback: NaN });
  return Number.isFinite(n) ? n : '';
};

const sanitizeFieldValue = (field, value) => {
  if (field === 'dealModelId') {
    return DEAL_MODEL_MAP[value] ? value : null;
  }

  if (field === 'targetMonths') {
    const months = Number(value);
    return ALLOWED_MONTHS.has(months) ? months : 6;
  }

  if (NUMERIC_FIELDS.has(field)) {
    return sanitizeNumericField(value);
  }

  return value;
};

const sanitizeStoredState = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const nicheId = isValidNiche(raw.nicheId) ? raw.nicheId : null;
  const dealModelId = DEAL_MODEL_MAP[raw.dealModelId] ? raw.dealModelId : null;

  return {
    nicheId,
    dealModelId,
    revenue: sanitizeNumericField(raw.revenue),
    sales: sanitizeNumericField(raw.sales),
    leads: sanitizeNumericField(raw.leads),
    frequency: sanitizeNumericField(raw.frequency),
    keyMetric: sanitizeNumericField(raw.keyMetric),
    optionalMetric: sanitizeNumericField(raw.optionalMetric),
    adBudget: sanitizeNumericField(raw.adBudget),
    targetRevenue: sanitizeNumericField(raw.targetRevenue),
    targetMonths: ALLOWED_MONTHS.has(Number(raw.targetMonths)) ? Number(raw.targetMonths) : 6,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_NICHE': {
      if (!isValidNiche(action.nicheId)) return state;
      const niche = getNiche(action.nicheId);
      return {
        ...state,
        nicheId: action.nicheId,
        dealModelId: niche ? niche.defaultDealModel : state.dealModelId,
        keyMetric: niche && niche.keyMetric ? niche.keyMetric.defaultValue : '',
        optionalMetric: '',
      };
    }

    case 'SET_FIELD': {
      if (!ALLOWED_FIELDS.has(action.field)) return state;
      return { ...state, [action.field]: sanitizeFieldValue(action.field, action.value) };
    }

    case 'SET_SIMULATION': {
      if (!ALLOWED_LEVERS.has(action.leverId)) return state;
      const value = sanitizeFiniteNumber(action.value, { min: 0, max: 5, fallback: 0 });
      return {
        ...state,
        simulation: { ...state.simulation, [action.leverId]: value },
      };
    }

    case 'RESET_SIMULATION':
      return { ...state, simulation: {} };

    case 'GO_TO':
      if (!STEP_ORDER.includes(action.step)) return state;
      return { ...state, step: action.step };

    case 'RESTORE': {
      const cleaned = sanitizeStoredState(action.payload);
      if (!cleaned) return state;
      return { ...state, ...cleaned, step: state.step, simulation: {} };
    }

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

const readStoredState = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw || raw.length > MAX_STORAGE_BYTES) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};


/**
 * Состояние диагностики. Черновик хранится в localStorage,
 * чтобы случайно закрытая вкладка не стирала введённые цифры.
 */
const useDiagnosticForm = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const stored = readStoredState();
    if (stored) {
      dispatch({ type: 'RESTORE', payload: stored });
    }
  }, []);

  useEffect(() => {
    try {
      const { step, simulation, ...persisted } = state;
      const cleaned = sanitizeStoredState(persisted);
      if (!cleaned) return;
      const serialized = JSON.stringify(cleaned);
      if (serialized.length > MAX_STORAGE_BYTES) return;
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
      // Приватный режим или переполненное хранилище — работаем без черновика.
    }
  }, [state]);

  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const setNiche = useCallback((nicheId) => {
    dispatch({ type: 'SET_NICHE', nicheId });
  }, []);

  const setSimulation = useCallback((leverId, value) => {
    dispatch({ type: 'SET_SIMULATION', leverId, value });
  }, []);

  const resetSimulation = useCallback(() => {
    dispatch({ type: 'RESET_SIMULATION' });
  }, []);

  const goTo = useCallback((step) => {
    dispatch({ type: 'GO_TO', step });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Хранилище недоступно — состояние всё равно сброшено в памяти.
    }
  }, []);

  const niche = useMemo(() => getNiche(state.nicheId), [state.nicheId]);

  /** Частота берётся из нишевой метрики, если именно она её измеряет. */
  const frequencyInput = useMemo(() => {
    if (state.frequency !== '') return state.frequency;
    if (niche && niche.keyMetric && niche.keyMetric.id === 'visitFrequency') {
      const perYear = Number(state.keyMetric);
      return Number.isFinite(perYear) && perYear > 0 ? perYear / 12 : '';
    }
    return '';
  }, [state.frequency, state.keyMetric, niche]);

  const result = useMemo(() => {
    if (!state.nicheId || state.revenue === '' || state.sales === '') {
      return null;
    }

    return runDiagnostic({
      nicheId: state.nicheId,
      dealModelId: state.dealModelId,
      revenue: state.revenue,
      sales: state.sales,
      leads: state.leads,
      frequency: frequencyInput,
      targetRevenue: state.targetRevenue,
      targetMonths: state.targetMonths,
      adBudget: state.adBudget,
      keyMetric: state.keyMetric,
      optionalMetric: state.optionalMetric,
    });
  }, [state, frequencyInput]);

  const canProceed = useMemo(() => {
    switch (state.step) {
      case STEP.NICHE:
        return Boolean(state.nicheId);
      case STEP.DEAL_MODEL:
        return Boolean(state.dealModelId);
      case STEP.NUMBERS:
        return Boolean(result && result.valid);
      case STEP.GOAL:
        return Number(state.targetRevenue) > 0;
      default:
        return true;
    }
  }, [state.step, state.nicheId, state.dealModelId, state.targetRevenue, result]);

  const stepIndex = STEP_ORDER.indexOf(state.step);

  const next = useCallback(() => {
    const index = STEP_ORDER.indexOf(state.step);
    if (index < STEP_ORDER.length - 1) {
      goTo(STEP_ORDER[index + 1]);
    }
  }, [state.step, goTo]);

  const back = useCallback(() => {
    const index = STEP_ORDER.indexOf(state.step);
    if (index > 0) {
      goTo(STEP_ORDER[index - 1]);
    }
  }, [state.step, goTo]);

  return {
    state,
    niche,
    result,
    stepIndex,
    canProceed,
    setField,
    setNiche,
    setSimulation,
    resetSimulation,
    goTo,
    next,
    back,
    reset,
  };
};

export default useDiagnosticForm;
